import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import fetch from '../../../../utils/fetch.js';
import SearchInputField from './SearchInputField';
import SuggestedSearches from './SuggestedSearches';
import InfoMessage from './InfoMessage';
import { AUTO_ADDRESS_API_KEY, AUTO_ADDRESS_GIFTS_API_KEY } from '../../../../utils/constants';

import styles from './index.module.scss';

const debug = require('debug')('cw:AddressAutocompleteSearchIE.js');

/**
 * A stateful component that manages a IE address format and provides APIs to autocomplete
 * and autofill addresses.
 *
 * See https://autoaddress2.helpscoutdocs.com/category/6-api for address lookup API results docs.
 */
const AddressAutocompleteSearchIE = ({
  onAddressSearch,
  hideSearchField = false,
  searchInfoText = '',
  isRequired = false,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [submitOptions, setSubmitOptions] = useState([]);
  const [infoMessage, setInfoMessage] = useState('');
  const [infoMessageVisible, setInfoMessageVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  // The reference of a timeout that makes sure the autocomplete lookup isn't called on
  // every keystroke.
  let postcodeDebouncer = null;

  // Minimum number of characters required for the lookup.
  const MIN_CHAR_LOOKUP = 3;

  useEffect(() => {
    if (window.location.host.startsWith('gifts')) {
      setApiKey(AUTO_ADDRESS_GIFTS_API_KEY);
    } else {
      setApiKey(AUTO_ADDRESS_API_KEY);
    }
  }, []);

  useEffect(() => {
    if (!hideSearchField) {
      setInfoMessageVisible(false);
      setSearchValue('');
    }
  }, [hideSearchField]);

  // Autocomplete dropdown values.
  useEffect(() => {
    // Clear submitOptions if they are exist to hide them.
    setSubmitOptions([]);
    // Hide message when a user continues using search.
    setInfoMessageVisible(false);
    // Don't need to do execute API requests for the previous query when query was changed.
    clearTimeout(postcodeDebouncer);
    if (searchValue.length >= MIN_CHAR_LOOKUP) {
      postcodeDebouncer = setTimeout(() => {
        fetch(
          `https://api.autoaddress.ie/2.0/autocomplete?key=${apiKey}${
            transactionId ? `&txn=${transactionId}` : ''
          }&address=${searchValue}&addressProfileName=Concern_Default&vanityMode=true`,
          {
            timeout: 12000,
          },
        )
          .then((resp) => {
            // If FindAddress was submitted during the Autocomplete request
            // no need to show autocomplete options.
            if (isLoading) {
              return;
            }

            if (resp.status === 200) {
              // Save transaction ID to combine all Autocomplete/FindAddress API requests.
              if (!transactionId && resp.body?.input?.txn) {
                setTransactionId(resp.body.input.txn);
              }

              // Display Autocomplete options if they exist.
              const options = resp.body.options.map((option) => ({
                ...option,
                title: option.displayName,
              }));
              setAutocompleteOptions(options);
            } else {
              // The API might respond with an error code.
              debug(resp);
            }
          })
          .catch((error) => {
            // Might be because API is down or region-blocked. Options can still be added.
            debug(error);
          });
      }, 400);
    } else {
      clearTimeout(postcodeDebouncer);
      setAutocompleteOptions([]);
    }
    return () => {
      clearTimeout(postcodeDebouncer);
    };
  }, [searchValue]);

  const findAddress = (url) => {
    // No need to send Autocomplete API request.
    clearTimeout(postcodeDebouncer);
    setIsLoading(true);
    // Clear autocompleteOptions if they exist.
    setAutocompleteOptions([]);
    fetch(url, {
      timeout: 12000,
    })
      .then((resp) => {
        setIsLoading(false);
        if (resp.status === 200) {
          // Save transaction ID to combine all Autocomplete/FindAddress API requests.
          if (!transactionId && resp.body?.input?.txn) {
            setTransactionId(resp.body.input.txn);
          }

          onAddressSearch(resp);

          // Display Address options if they exist or show a message.
          if (resp.body.totalOptions > 0) {
            const options = resp.body.options.map((option) => ({
              ...option,
              title: option.displayName,
            }));
            // Clear autocompleteOptions if they exist.
            setAutocompleteOptions([]);

            setSubmitOptions(options);
            if (options.length) {
              setInfoMessage('Please select an option below');
            } else {
              setInfoMessage('Too little information, please enter more details');
            }
            setInfoMessageVisible(true);
          } else {
            // Display different messages depending on status code.
            switch (resp.body.result.code) {
              case 150:
                setInfoMessage(
                  "Address found, populated below. Make sure it's correct before continuing",
                );
                break;
              case 400:
              case 300:
              case 200:
                setInfoMessage(
                  "We've found the address, please add your Postcode in the text box below if you know it",
                );
                break;
              case 210:
                setInfoMessage('Postcode Address no longer exits');
                break;
              case 550:
              case 700:
                setInfoMessage('No results found, please enter it manually below');
                break;
              case 600:
                setInfoMessage(
                  'The address provided is not an Irish address. Please make sure you are using the correct country above',
                );
                break;
              default:
                if (resp.body.postcode) {
                  setInfoMessage('Address found, populated below');
                } else {
                  setInfoMessage(
                    "We've found the address, please add your Postcode in the text box below if you know it",
                  );
                }
                break;
            }

            setInfoMessageVisible(true);
            // Hide submitOptions if they exist.
            setSubmitOptions([]);
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);
        // Silent error. Happens when API limit is exceeded, or when an invalid
        // postcode is submitted, or when there is any higher-level API error
        // (unavailablity or blocked).
        debug(error);
      });
  };

  const onOptionClick = (option) => {
    findAddress(option.links[0].href);
  };

  const onSearchButtonSubmit = (value) => {
    if (value.trim().length < MIN_CHAR_LOOKUP) {
      setInfoMessage('Too little information, please enter more details');
      setInfoMessageVisible(true);
    } else {
      findAddress(
        `https://api.autoaddress.ie/2.0/findaddress?key=${apiKey}${
          transactionId ? `&txn=${transactionId}` : ''
        }&address=${value}&addressProfileName=Concern_Default&vanityMode=true`,
      );
    }
  };

  return (
    <div id="autocomplete-search-ie" className={styles['autocomplete-search']}>
      <div className={styles['autocomplete-search__container']}>
        {!hideSearchField && (
          <SearchInputField
            isRequired={isRequired}
            onSearchButtonSubmit={onSearchButtonSubmit}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Enter your address or Eircode"
            label="Address search"
            info={searchInfoText}
            autocompleteOptions={autocompleteOptions}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (autocompleteOptions.length && window.innerWidth > 575) {
                  onOptionClick(autocompleteOptions[0]);
                } else {
                  onSearchButtonSubmit(searchValue);
                }
              }
            }}
            onOptionClick={onOptionClick}
            isLoading={isLoading}
          />
        )}

        {infoMessageVisible && (
          <InfoMessage onClose={() => setInfoMessageVisible(false)} text={infoMessage} />
        )}

        {!!submitOptions.length && (
          <SuggestedSearches
            onOptionClick={onOptionClick}
            options={submitOptions}
            className={styles['autocomplete-search__suggested-addresses']}
          />
        )}
      </div>
    </div>
  );
};

AddressAutocompleteSearchIE.propTypes = {
  onAddressSearch: PropTypes.func.isRequired,
  hideSearchField: PropTypes.bool,
  searchInfoText: PropTypes.string,
  isRequired: PropTypes.bool,
};

export default AddressAutocompleteSearchIE;
